import React, { useCallback, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import {
  Alert,
  Box,
  Chip,
  Divider,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { FormattedMessage, injectIntl } from 'react-intl';
import ApiClient from '../../loopback/apiClient';
import {
  fetchDigitalGroundTruthGroups,
  fetchDigitalSectionScores,
  fetchDigitalSurveyScores,
  fetchDigitalSubmissionsWithMetas,
  fetchFullDigitalSurvey,
  fetchInstituteDigitalSubmissions,
  fetchSurveySubmissionMeta,
  setDigitalSubmissionMeta,
  setSelectedDigitalGroundTruthGroup,
  setSelectedDigitalsection,
  fetchDigitalGroundTruths,
} from '../../redux/actions';
import {
  selectDigitalCases,
  selectDigitalGTDictionaryByQuestionAndCase,
  selectDigitalQuestionsBySection,
  selectDigitalSections,
  selectDigitalSubmissionByQuestionAndCase,
  selectDigitalSubmissionMetaBySection,
  selectDigitalSurveyScoreBasedOnGTGroupId,
  selectSectionScoreBasedOnSectionAndGTGroupId,
  selectSelectedSection,
  selectSettingsDictionaryByQuestionAndCase,
} from '../../redux/reducers/SurveyDataReducer';
import {
  AggregatedSubmission,
  DigitalSection,
  Enrollment,
  ExtendedPayload,
  Institute,
  OverviewSurveySubmission,
  QuipUser,
  QuipUserWithInstitute,
  RootState,
} from '../../redux/types';
import ReviewSubmissionTable from '../review/ReviewSubmissionTable';
import SubmissionAggregation from '../submissionAggregation/SubmissionAggregation';
import useGetTrialById from '../../hooks/trial/useGetTrialById';
import FaerbungReview from '../review/reviewCategory/faerbungReview/FaerbungReview';
import { AuswertungSetting } from '../../redux/types/auswertungSettingTypes';
import MultiReviewComments from '../review/reviewCategory/textblockReview/MultiReviewComments';
import {
  selectFaerbungOverallPassScore,
  selectFaerbungReviewScoreBySectionId,
  selectSelectedFaerbungReviewMeta,
  selectTotalFaerbungReviewScore,
} from '../../redux/reducers/FaerbungReviewReducer';
import { Colors } from '../utilities/Colors';
import { convertToQid } from '../../utilities/stringHelper';

type SummaryDetailsAdminProps = {
  enrollmentId: number;
  sampleset?: string;
  quipUser: QuipUser;
  institute: Institute;
  finalizedStatus?: boolean;
};

const SummaryDetailsAdmin: React.FC<SummaryDetailsAdminProps> = ({
  enrollmentId,
  sampleset,
  quipUser,
  institute,
  finalizedStatus,
}: SummaryDetailsAdminProps) => {
  const country = institute.country || quipUser.userCountry;
  const qId = convertToQid(quipUser.id, country);
  return (
    <>
      <Box display="flex">
        <Typography variant="body2" flexBasis="11em" color="grey.600">
          Enrollment ID
        </Typography>
        <Typography variant="body2">{enrollmentId}</Typography>
      </Box>
      {sampleset && (
        <Box display="flex">
          <Typography variant="body2" flexBasis="11em" color="grey.600">
            Set Number ID
          </Typography>
          <Typography variant="body2">{sampleset}</Typography>
        </Box>
      )}
      <Box display="flex">
        <Typography variant="body2" flexBasis="11em" color="grey.600">
          Quip User ID
        </Typography>
        <Typography variant="body2">{qId}</Typography>
      </Box>
      <Box display="flex">
        <Typography variant="body2" flexBasis="11em" color="grey.600">
          Institute ID
        </Typography>
        <Typography variant="body2">{institute.id}</Typography>
      </Box>
      <Box display="flex">
        <Typography variant="body2" flexBasis="11em" color="grey.600">
          Submission Finalized
        </Typography>
        <Typography variant="body2">
          {finalizedStatus ? 'True' : 'False'}
        </Typography>
      </Box>
    </>
  );
};

type SummaryDetailsProps = {
  overview: OverviewSurveySubmission;
  auswertungSetting?: AuswertungSetting;
};

const SummaryDetails: React.FC<SummaryDetailsProps> = ({
  overview,
  auswertungSetting,
}: SummaryDetailsProps) => {
  const {
    successRate,
    failedRate,
    success,
    failed,
    finalized,
    enrollment,
  } = overview;
  return (
    <>
      {!auswertungSetting?.hideSuccessRate && (
        <Box display="flex">
          <Typography variant="body2" flexBasis="11em" color="grey.600">
            Success Rate
          </Typography>
          <Typography variant="body2">{`${success}/${finalized} (${successRate}%)`}</Typography>
        </Box>
      )}
      {!auswertungSetting?.hideFailRate && (
        <Box display="flex">
          <Typography variant="body2" flexBasis="11em" color="grey.600">
            Failure Rate
          </Typography>
          <Typography variant="body2">{`${failed}/${finalized} (${failedRate}%)`}</Typography>
        </Box>
      )}
      {!auswertungSetting?.hideParticipantCount && (
        <Box display="flex">
          <Typography variant="body2" flexBasis="11em" color="grey.600">
            Total Submission
          </Typography>
          <Typography variant="body2">{`${finalized}/${enrollment}`}</Typography>
        </Box>
      )}
    </>
  );
};

type SectionDetailsProps = {
  selectedSection: DigitalSection;
  overview: OverviewSurveySubmission;
  auswertungSetting?: AuswertungSetting;
};
const SectionDetails: React.FC<SectionDetailsProps> = ({
  selectedSection,
  overview,
  auswertungSetting,
}: SectionDetailsProps) => {
  const { sectionsSummary } = overview;
  const { id: sectionId, name } = selectedSection;
  const summary = sectionsSummary[sectionId];
  const { pass = 0, total = 0, finalized = 0 } = summary || {};
  return (
    <>
      <Typography display="flex" sx={{ mt: 2, mb: 1 }}>
        {name}
      </Typography>
      <Box display="flex">
        <Typography variant="body2" flexBasis="11em" color="grey.600">
          Success Rate
        </Typography>
        <Typography variant="body2">
          {`${pass}/${finalized} (${((pass / finalized) * 100).toFixed(2)}%)`}
        </Typography>
      </Box>
      {!auswertungSetting?.hideParticipantCount && (
        <Box display="flex">
          <Typography variant="body2" flexBasis="11em" color="grey.600">
            Total Submission
          </Typography>
          <Typography variant="body2">{`${finalized}/${total}`}</Typography>
        </Box>
      )}
    </>
  );
};

const PassChip: React.FC = () => (
  <Chip color="success" size="small" label={<FormattedMessage id="pass" />} />
);
const FailChip: React.FC = () => (
  <Chip color="error" size="small" label={<FormattedMessage id="fail" />} />
);

const DigitalEvaluationDialog: React.FC<Props> = ({
  trialId,
  trialName,
  enrollmentId,
  survey,
  admin,
  sections,
  selectedSection,
  digitalSectionScore,
  digitalSurveyScore,
  questions,
  groundTruthGroupId,
  cases,
  submissionsMapByQuestionAndCaseId,
  sectionSubmissionMeta,
  surveySubmissionMeta,
  aggregatedSubmission,
  settingsDictionary,
  auswertungSetting,
  faerbungReviewSectionScore,
  totalFaerbungReviewScore,
  faerbungReviewMeta,
  overallFaerbungReviewPassScore,
  gtDictionaryByQuestionAndCase,
  fetchFullDigitalSurvey: dispatchFetchFullDigitalSurvey,
  fetchDigitalSubmissionsWithMetas: dispatchFetchDigitalSubmissionsWithMetas,
  fetchInstituteDigitalSubmissions: dispatchFetchInstituteDigitalSubmissions,
  setDigitalSubmissionMeta: dispatchSetDigitalSubmissionMeta,
  setSelectedDigitalsection: dispatchSetSelectedDigitalSection,
  fetchSurveySubmissionMeta: dispatchFetchSurveySubmissionMeta,
  fetchDigitalSectionScores: dispatchFetchDigitalSectionScores,
  fetchDigitalSurveyScores: dispatchFetchDigitalSurveyScores,
  fetchDigitalGroundTruthGroups: dispatchFetchDigitalGroundTruthGroups,
  fetchDigitalGroundTruths: dispatchFetchDigitalGroundTruths,
  setSelectedDigitalGroundTruthGroup: dispatchSetSelectedDigitalGroundTruthGroup,
  intl,
}: Props) => {
  const [enrollment, setEnrollment] = useState<Enrollment>();
  const [userWithInstitute, setUserWithInstitute] = useState<
    QuipUserWithInstitute
  >();
  const [overviewSubmission, setOverviewSubmission] = useState<
    OverviewSurveySubmission
  >();
  const [textblockComment, setTextblockComment] = useState<
    ExtendedPayload['sections']
  >([]);
  const trial = useGetTrialById(Number(trialId));

  /** Loads data in redux when the user is admin */
  const adminBootstrap = useCallback(() => {
    ApiClient.getDigitalSurveyByTrialId(Number(trialId))
      .then((surveys) => surveys.filter((ds) => !ds.exercise))
      .then((selectedSurvey) => dispatchFetchFullDigitalSurvey(selectedSurvey[0].id));
    ApiClient.getEnrollments<Enrollment>(
      Number(trialId),
      { id: Number(enrollmentId), status: 'approved' },
      null,
    )
      .then((e) => {
        const enrollmentObj = e[0];
        setEnrollment(enrollmentObj);
        dispatchFetchSurveySubmissionMeta(enrollmentObj.id);
        dispatchFetchDigitalSubmissionsWithMetas(enrollmentObj.id);
        return enrollmentObj;
      })
      .then((e) => ApiClient.getUserWithInstitute(e.attendeeId))
      .then((u) => {
        setUserWithInstitute(u);
      });
  }, [
    trialId,
    enrollmentId,
    dispatchFetchFullDigitalSurvey,
    dispatchFetchSurveySubmissionMeta,
    dispatchFetchDigitalSubmissionsWithMetas,
  ]);

  /** Loads data in redux when user is not admin */
  const userBootstrap = useCallback(() => {
    ApiClient.getDigitalSurveyByTrialId(Number(trialId))
      .then((surveys) => surveys.filter((ds) => !ds.exercise))
      .then((selectedSurvey) => dispatchFetchFullDigitalSurvey(selectedSurvey[0].id));
    dispatchFetchSurveySubmissionMeta(enrollmentId);
    dispatchFetchInstituteDigitalSubmissions(trialId);
    ApiClient.findOrCreateSubmissionMetaForAllSections(trialId)
      .then((dsms) => dispatchSetDigitalSubmissionMeta(dsms));
  }, [
    trialId,
    enrollmentId,
    dispatchFetchFullDigitalSurvey,
    dispatchFetchSurveySubmissionMeta,
    dispatchFetchInstituteDigitalSubmissions,
    dispatchSetDigitalSubmissionMeta,
  ]);

  useEffect(() => {
    if (admin) {
      adminBootstrap();
    } else {
      userBootstrap();
    }
    ApiClient.getOverviewSurveySubmission(trialId).then((overviewSub) => {
      setOverviewSubmission(overviewSub);
    });
  }, [admin, adminBootstrap, userBootstrap, setOverviewSubmission, trialId]);

  useEffect(() => {
    if (!selectedSection && sections.length > 0) {
      dispatchSetSelectedDigitalSection(sections[0].id);
    }
  }, [selectedSection, dispatchSetSelectedDigitalSection, sections]);

  useEffect(() => {
    const setGTGroups = async () => {
      if (groundTruthGroupId && survey?.id) {
        await dispatchFetchDigitalGroundTruthGroups(survey.id);
        await dispatchFetchDigitalGroundTruths(survey.id);
        await dispatchFetchDigitalSurveyScores(survey.id);
        await dispatchFetchDigitalSectionScores(survey.id);
        await dispatchSetSelectedDigitalGroundTruthGroup(groundTruthGroupId);
      }
    };
    setGTGroups();
  }, [
    dispatchFetchDigitalGroundTruthGroups,
    dispatchFetchDigitalGroundTruths,
    dispatchFetchDigitalSurveyScores,
    dispatchFetchDigitalSectionScores,
    dispatchSetSelectedDigitalGroundTruthGroup,
    groundTruthGroupId,
    survey?.id,
  ]);

  useEffect(() => {
    /** Loading Textblock comments */
    if (trial && trial.reviewCategoryId !== 3) {
      return;
    }
    ApiClient.getTextblockReviewCommentByEnrollmentId(enrollmentId).then(
      (c) => {
        if (c.length) {
          setTextblockComment(c[0].payload.sections);
        }
      },
    );
  }, [enrollmentId, trial]);

  const handleReviewTabSwitch = (
    event: React.SyntheticEvent,
    newValue: number,
  ) => {
    dispatchSetSelectedDigitalSection(newValue);
  };

  const isOptOut = sectionSubmissionMeta?.isOptOut;
  const sectionResult = sectionSubmissionMeta?.passed ? (
    <PassChip />
  ) : (
    <FailChip />
  );
  const surveyResult = surveySubmissionMeta?.passed ? (
    <PassChip />
  ) : (
    <FailChip />
  );
  const { reviewScore: surveyReviewScore } = surveySubmissionMeta || {};
  const { reviewScore: sectionReviewScore } = sectionSubmissionMeta || {};
  const { maxScore: sectionMaxScore, passScore: sectionPassScore } = digitalSectionScore || {};
  const {
    maxScore: digitalSurveyMaxScore, passScore: digitalSurveyPassScore,
  } = digitalSurveyScore || {};

  return (
    <>
      <Box sx={{ overflow: 'auto' }}>
        <Typography variant="h5" sx={{ mx: 2, mt: 2 }}>
          <FormattedMessage
            id="enrollmentsmanager.detailedEvaluation"
            values={{ trial: trialName }}
          />
        </Typography>
        <Box sx={{ my: 2, mx: 4 }}>
          {admin && userWithInstitute && enrollment && (
            <SummaryDetailsAdmin
              enrollmentId={enrollment.id}
              sampleset={enrollment.sampleset?.toString()}
              quipUser={userWithInstitute}
              institute={userWithInstitute?.institute}
              finalizedStatus={surveySubmissionMeta?.finalized}
            />
          )}
          {!!overviewSubmission && (
            <SummaryDetails
              overview={overviewSubmission}
              auswertungSetting={auswertungSetting}
            />
          )}
          {!!overviewSubmission
            && selectedSection
            && sections.length > 1
            && !auswertungSetting?.hideSectionSummary && (
              <SectionDetails
                selectedSection={selectedSection}
                overview={overviewSubmission}
                auswertungSetting={auswertungSetting}
              />
          )}
        </Box>
        <Divider />
        {surveySubmissionMeta === undefined && (
          <Alert sx={{ mt: 1 }} severity="warning">
            <FormattedMessage id="evaluationdialog.noSubmissions" />
          </Alert>
        )}
        {selectedSection && (
          <Box sx={{ mt: 1 }}>
            <Tabs
              value={selectedSection?.id}
              onChange={handleReviewTabSwitch}
              variant="scrollable"
              scrollButtons
            >
              {sections.map((s) => (
                <Tab label={s.name} key={s.id} value={s.id} />
              ))}
            </Tabs>
          </Box>
        )}
        {selectedSection?.optional && (
          <Alert sx={{ mt: 1 }} severity="info">
            <FormattedMessage id="evaluationdialog.optionalSection" />
          </Alert>
        )}
        <Box sx={{ height: '100%', p: 2 }}>
          <ReviewSubmissionTable
            mode="auswertung"
            digitalCases={cases}
            digitalQuestions={questions}
            submissionsMapByQuestionAndCaseId={
              submissionsMapByQuestionAndCaseId
            }
            settingsDictionary={settingsDictionary}
            showLegend
            DGTDictionaryByQuestionAndCase={gtDictionaryByQuestionAndCase}
          />
        </Box>
        <Box
          sx={{
            my: 2,
            py: 2,
            px: 4,
            backgroundColor: 'grey.100',
          }}
        >
          <Box display="flex" gap={10}>
            <Box>
              <Typography fontWeight={500}>
                <FormattedMessage id="review.minScoreHeadline" />
              </Typography>
              <Box display="flex" alignItems="center">
                <Typography>
                  <FormattedMessage id="section" />
                  {` ${sectionPassScore}`}
                </Typography>
              </Box>
              <Typography>
                <FormattedMessage id="overall" />
                {` ${digitalSurveyPassScore}`}
              </Typography>
              {trial?.reviewCategoryId === 2 && (
                <Box component="span">
                  <Typography>
                    <FormattedMessage id="review.sectionStainingReview" />
                    {faerbungReviewMeta?.passingScore}
                  </Typography>
                  <Typography>
                    <FormattedMessage id="review.surveyStainingReview" />
                    {overallFaerbungReviewPassScore}
                  </Typography>
                </Box>
              )}
            </Box>
            <Box>
              <Typography fontWeight={500}>
                <FormattedMessage id="review.obtainedScoreHeadline" />
              </Typography>
              <Box display="flex" alignItems="center" sx={{ mb: '0.1em' }}>
                <Typography flexBasis={150}>
                  <FormattedMessage id="section" />
                  {sectionSubmissionMeta
                    ? ` ${sectionSubmissionMeta.acquiredScore} / ${sectionMaxScore} `
                    : 'N/A'}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center">
                {!auswertungSetting?.hideOverallScore && (
                  <>
                    <Typography flexBasis={150}>
                      <FormattedMessage id="overall" />
                      {surveySubmissionMeta
                        ? ` ${surveySubmissionMeta.acquiredScore} / ${digitalSurveyMaxScore} `
                        : 'N/A'}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
            <Box>
              <Typography fontWeight={500}>
                <FormattedMessage id="review.reviewScoreHeadline" />
              </Typography>
              <Typography sx={{ mb: '0.1em' }}>
                <FormattedMessage id="section" />
                {` ${
                  sectionReviewScore
                  || intl.formatMessage({ id: 'review.noReview' })
                }`}
              </Typography>
              <Typography>
                <FormattedMessage id="overall" />
                {` ${
                  surveyReviewScore
                  || intl.formatMessage({ id: 'review.noReview' })
                }`}
              </Typography>
              {trial?.reviewCategoryId === 2 && (
                <Box component="span">
                  <Typography>
                    <FormattedMessage id="review.sectionStainingReview" />
                    {faerbungReviewSectionScore}
                  </Typography>
                  <Typography>
                    <FormattedMessage id="review.surveyStainingReview" />
                    {totalFaerbungReviewScore}
                  </Typography>
                </Box>
              )}
            </Box>
            <Box>
              <Typography fontWeight={500}>Status</Typography>
              <Box sx={{ mb: '0.1em' }}>
                <Typography component="span">
                  <FormattedMessage id="review.sectionStatus" />
                </Typography>
                <Typography
                  fontWeight={500}
                  color={
                    sectionSubmissionMeta?.passed
                      ? Colors.correct
                      : Colors.wrong
                  }
                  component="span"
                >
                  {isOptOut ? (
                    <Chip color="warning" size="small" label="Opt-Out" />
                  ) : (
                    sectionResult
                  )}
                </Typography>
              </Box>
              <Box sx={{ mb: '0.1em' }}>
                <Typography component="span">
                  <FormattedMessage id="review.overallStatus" />
                </Typography>
                <Typography
                  fontWeight={500}
                  component="span"
                  color={
                    surveySubmissionMeta?.passed ? Colors.correct : Colors.wrong
                  }
                >
                  {surveyResult}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box sx={{ m: 2 }}>
          <Box sx={{ m: 2 }}>
            <Box display="flex" sx={{ my: 1 }} alignItems="center">
              <Typography flexBasis={200}>
                <FormattedMessage id="review.sectionCommentHeadline" />
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  maxHeight: 100,
                  overflow: 'auto',
                  flexGrow: 1,
                }}
              >
                <Typography whiteSpace="pre-wrap" component="p" gutterBottom>
                  {sectionSubmissionMeta?.reviewComment}
                </Typography>
              </Paper>
            </Box>
            <Box display="flex" sx={{ my: 1 }} alignItems="center">
              <Typography flexBasis={200}>
                <FormattedMessage id="review.surveyCommentHeadline" />
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  maxHeight: 100,
                  overflow: 'auto',
                  flexGrow: 1,
                }}
              >
                <Typography whiteSpace="pre-wrap" component="p">
                  {surveySubmissionMeta?.reviewComment}
                </Typography>
              </Paper>
            </Box>
          </Box>
        </Box>
        <Divider />
        {trial && trial.reviewCategoryId === 2 && survey && selectedSection && (
          <Box sx={{ p: 2 }}>
            <FaerbungReview
              caseList={cases}
              surveyId={survey.id}
              sectionId={selectedSection.id}
              enrollmentId={Number(enrollmentId)}
            />
          </Box>
        )}
        {trial
          && trial.reviewCategoryId === 3
          && textblockComment
          && survey
          && selectedSection && (
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                <FormattedMessage id="trialSetup.textblockCommentTitle" />
              </Typography>
              <MultiReviewComments
                sectionComments={
                  textblockComment[
                    sections.findIndex((s) => s.id === selectedSection.id)
                  ]
                }
              />
            </Box>
        )}
        {aggregatedSubmission && aggregatedSubmission.released && (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6">Submission Aggregations</Typography>
            <SubmissionAggregation
              digitalCases={cases}
              digitalQuestions={questions.filter((q) => !q.hideAggregation)}
              selectedSectionId={selectedSection?.id}
              aggregatedSubmission={aggregatedSubmission?.payload}
              settingsDictionary={settingsDictionary}
            />
          </Box>
        )}
      </Box>
    </>
  );
};

type AdminProps = {
  trialId: number;
  trialName: string;
  enrollmentId: number;
  aggregatedSubmission?: AggregatedSubmission;
  admin: true;
};

type UserProps = {
  trialId: number;
  trialName: string;
  enrollmentId: number;
  aggregatedSubmission?: AggregatedSubmission;
  admin: false;
};

const mapStateToProps = (
  state: RootState,
  ownProps: AdminProps | UserProps,
) => ({
  admin: ownProps.admin,
  aggregatedSubmission: ownProps.aggregatedSubmission,
  cases: selectDigitalCases(state),
  digitalSectionScore: selectSectionScoreBasedOnSectionAndGTGroupId(state),
  digitalSurveyScore: selectDigitalSurveyScoreBasedOnGTGroupId(state),
  enrollmentId: ownProps.enrollmentId,
  questions: selectDigitalQuestionsBySection(state),
  sectionSubmissionMeta: selectDigitalSubmissionMetaBySection(state),
  sections: selectDigitalSections(state),
  selectedSection: selectSelectedSection(state),
  submissionMetas: state.SurveyData.digitalSubmissionMeta.byId,
  submissionsMapByQuestionAndCaseId: selectDigitalSubmissionByQuestionAndCase(
    state,
  ),
  survey: state.SurveyData.survey,
  surveySubmissionMeta: state.SurveyData.surveySubmissionMeta,
  trialId: ownProps.trialId,
  trialName: ownProps.trialName,
  settingsDictionary: selectSettingsDictionaryByQuestionAndCase(state),
  faerbungReviewSectionScore: selectFaerbungReviewScoreBySectionId(
    state,
    state.SurveyData.digitalSections.selectedSection,
  ),
  totalFaerbungReviewScore: selectTotalFaerbungReviewScore(state),
  faerbungReviewMeta: selectSelectedFaerbungReviewMeta(
    state,
    state.SurveyData.digitalSections.selectedSection,
  ),
  overallFaerbungReviewPassScore: selectFaerbungOverallPassScore(state),
  gtDictionaryByQuestionAndCase: selectDigitalGTDictionaryByQuestionAndCase(
    state,
    state.SurveyData.digitalSections.selectedSection,
  ),
});

const dispatchProps = {
  fetchDigitalGroundTruthGroups,
  fetchDigitalGroundTruths,
  fetchDigitalSectionScores,
  fetchDigitalSubmissionsWithMetas,
  fetchDigitalSurveyScores,
  fetchFullDigitalSurvey,
  fetchInstituteDigitalSubmissions,
  fetchSurveySubmissionMeta,
  setDigitalSubmissionMeta,
  setSelectedDigitalGroundTruthGroup,
  setSelectedDigitalsection,
};
const connector = connect(mapStateToProps, dispatchProps);
type Props = {
  intl: { formatMessage: (arg: Record<string, string>) => string };
  groundTruthGroupId?: number;
  auswertungSetting?: AuswertungSetting;
} & ConnectedProps<typeof connector>;

export default injectIntl(connector(DigitalEvaluationDialog));
